import React, { createContext, useContext, useState, ReactNode } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { Result, TransportError } from '@peercolab/engine';

type ToastContextType = {
  onError: <T>(result: Result<T>) => Result<T>;
  showToast: (message: TransportError, severity?: 'error' | 'success' | 'info' | 'warning') => void;
  showPlainToast: (message: string, severity?: 'error' | 'success' | 'info' | 'warning') => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<'error' | 'success' | 'info' | 'warning'>('error');

  const showToast = (msg: TransportError, sev: typeof severity = 'error') => {
    if (msg.details?.userError)
        setMessage(msg.details?.userError)
    else if (msg.details?.technicalError)
        setMessage(msg.details?.technicalError)
    else
        setMessage(msg.toShortString())
    console.log(msg.toLongString())
    setSeverity(sev);
    setOpen(true);
  };

  const showPlainToast = (msg: string, sev: typeof severity = 'error') => {
    setMessage(msg);
    if (severity === 'error' || severity === 'warning')
        console.log(msg)
    setSeverity(sev);
    setOpen(true);
  };

  const onError = <T,>(result: Result<T>): Result<T> => {
    if (result.error) {
      showToast(result.error, 'error');
    }
    return result;
  }

  return (
    <ToastContext.Provider value={{ showToast, showPlainToast, onError }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={4000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpen(false)} severity={severity} sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}